import React from 'react';
import { Card, Space, Button, Typography, Tag, Row, Col } from 'antd';
import { EditOutlined, DeleteOutlined, EyeOutlined, PhoneOutlined, MailOutlined } from '@ant-design/icons';
import { useOrganizationStatusRender } from './useOrganizationStatusRender';
import { useUserTimestampRender, useAvatarRender } from '@src/hooks';

const { Text } = Typography;

interface OrganizationCardViewProps {
  dataSource: any[];
  onEditClick?: (record: any) => void;
  onDeleteClick?: (record: any) => void;
  onViewClick?: (record: any) => void;
  onRowClick?: (record: any) => void;
  showEdit?: boolean;
  showDelete?: boolean;
  showView?: boolean;
  customActions?: Array<{
    key: string;
    icon: React.ReactNode;
    label: string;
    onClick: (record: any) => void;
    type?: 'primary' | 'default' | 'dashed' | 'link' | 'text';
    danger?: boolean;
    tooltip?: string;
    confirmMessage?: string;
  }>;
  rowKey?: string;
}

export const useOrganizationCardView = () => {
  const getStatusConfig = useOrganizationStatusRender();
  const userTimestampRender = useUserTimestampRender({
    size: 'small',
  });
  const avatarRender = useAvatarRender({
    size: 40,
    fontSize: '16px',
    useConsistentColors: true,
  });

  const OrganizationCardView: React.FC<OrganizationCardViewProps> = ({
    dataSource,
    onEditClick,
    onDeleteClick,
    onViewClick,
    onRowClick,
    showEdit = true,
    showDelete = true,
    showView = false,
    customActions = [],
    rowKey = 'id',
  }) => {
    // Helper function to render actions
    const renderActions = (record: any) => {
      const actions = [];

      if (showView) {
        actions.push(
          <Button
            key="view"
            type="text"
            size="small"
            icon={<EyeOutlined />}
            onClick={(e) => {
              e.stopPropagation();
              onViewClick?.(record);
            }}
            style={{ color: '#1890ff' }}
          />
        );
      }

      if (showEdit) {
        actions.push(
          <Button
            key="edit"
            type="text"
            size="small"
            icon={<EditOutlined />}
            onClick={(e) => {
              e.stopPropagation();
              onEditClick?.(record);
            }}
            style={{ color: '#faad14' }}
          />
        );
      }

      if (showDelete) {
        actions.push(
          <Button
            key="delete"
            type="text"
            size="small"
            icon={<DeleteOutlined />}
            onClick={(e) => {
              e.stopPropagation();
              onDeleteClick?.(record);
            }}
            style={{ color: '#ff4d4f' }}
          />
        );
      }

      // Add custom actions
      customActions.forEach((action) => {
        actions.push(
          <Button
            key={action.key}
            type="text"
            size="small"
            icon={action.icon}
            onClick={(e) => {
              e.stopPropagation();
              action.onClick(record);
            }}
            style={{ 
              color: action.danger ? '#ff4d4f' : action.type === 'primary' ? '#1890ff' : '#666'
            }}
          />
        );
      });

      return actions;
    };

    return (
      <div className="p-0">
        {dataSource && dataSource.length > 0 ? dataSource.map((record, index) => {
          const organizationName = record.name || 'Unknown Organization';
          const contactEmail = record.contact_email || '';
          const contactPhone = record.contact_mobile_no || '';
          const status = record.status || '';
          const address = record.address || '';
          const createdAt = record.created_at || '';

          return (
            <Card
              key={record[rowKey] || index}
              size="small"
              className={`
                mb-4 mx-4 
                ${index === 0 ? 'mt-4' : 'mt-0'}
                rounded-xl shadow-sm border border-gray-200 
                ${onRowClick ? 'cursor-pointer' : 'cursor-default'}
                transition-all duration-200 ease-in-out bg-white
              `}
              bodyStyle={{ padding: '16px' }}
              onClick={() => onRowClick?.(record)}
            >
              {/* Top Section - ID, Name, Status */}
              <Row gutter={[12, 8]} align="middle" className="mb-3">
                <Col flex="auto">
                  <Space size="small">
                    
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <Text className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                          #{record[rowKey]}
                        </Text>
                        <div className="text-base font-bold text-black leading-tight">
                          {organizationName}
                        </div>
                      </div>
                      {address && (
                        <div className="text-xs text-gray-600 mt-0.5">
                          {address}
                        </div>
                      )}
                    </div>
                  </Space>
                </Col>
                <Col>
                  <Space size="small">
                    {status && getStatusConfig(status)}
                  </Space>
                </Col>
              </Row>

              {/* Contact Information Section */}
              {(contactEmail || contactPhone) && (
                <Row gutter={[16, 8]} className="mb-3">
                  {contactEmail && (
                    <Col span={12}>
                      <div>
                        <Text type="secondary" className="text-xs">
                          Email
                        </Text>
                        <div className="flex items-center gap-1 mt-0.5">
                          <Text className="text-xs font-medium">
                            {contactEmail}
                          </Text>
                        </div>
                      </div>
                    </Col>
                  )}
                  {contactPhone && (
                    <Col span={12}>
                      <div>
                        <Text type="secondary" className="text-xs">
                          Phone
                        </Text>
                        <div className="flex items-center gap-1 mt-0.5">
                          <Text className="text-xs font-medium">
                            +{contactPhone}
                          </Text>
                        </div>
                      </div>
                    </Col>
                  )}
                </Row>
              )}

              {/* Bottom Section - Creator and Actions */}
              <Row justify="space-between" align="middle">
                <Col>
                  <Space size="small">
                    {userTimestampRender(record)}
                  </Space>
                </Col>
                <Col>
                  <Space size="small">
                    {renderActions(record)}
                  </Space>
                </Col>
              </Row>
            </Card>
          );
        }) : (
          <div className="py-10 px-5 text-center text-gray-500 text-sm">
            No organizations found
          </div>
        )}
      </div>
    );
  };

  return { OrganizationCardView };
};
